import { useEffect, useRef, useState } from 'react'
import {
	arePricesPending,
	type FestivalPricingInput,
	type TicketType,
} from '#app/domains/pricing/pure'
import {
	eventEnd,
	getVenuePinData,
	isEventPassed,
} from '#app/domains/program/pure'
import { isSoldOut, seatsRemaining } from '#app/domains/tickets/pure'
import { type EventWithSeatsAndTickets } from '#app/utils/providers/events.server.js'
import { DetailCtaCard } from '../organisms/event-detail/DetailCtaCard.tsx'
import { DetailHero } from '../organisms/event-detail/DetailHero.tsx'
import { StickyCtaBar } from '../organisms/event-detail/StickyCtaBar.tsx'
import { EventDescription } from '../organisms/event/EventDescription.tsx'
import { VenueNightMap } from '../organisms/program/VenueNightMap.tsx'

type DetailEvent = Omit<EventWithSeatsAndTickets, 'tickets' | 'seats'> & {
	seats: { total: number } | null
	tickets?: ReadonlyArray<{ paymentStatus?: string | null }> | null
	ticketCount?: number | null
	festival?: FestivalPricingInput | null
}

/**
 * A concert page: the night-sky hero, then the ticket card (sticky beside
 * the text on wide screens, right under the hero on phones), the
 * description and artists, and where it is on the Vorrå night map.
 */
export function EventDetailLayout({
	event,
	isIOS,
	initialQuantities,
	addOnArrival,
	inCart,
	reserved,
	vippsOnline,
	calendarUrl,
	shareUrl,
}: {
	event: DetailEvent
	isIOS: boolean
	initialQuantities?: Partial<Record<TicketType, number>>
	/** back from logging in: add the pre-filled tickets (see DetailCtaCard) */
	addOnArrival?: boolean
	inCart?: number
	reserved?: number
	/** Vipps payment on the site is live (see DetailCtaCard) */
	vippsOnline?: boolean
	calendarUrl?: string
	/** the page's absolute URL, for the share buttons */
	shareUrl?: string
}) {
	// only show the map when the venue has a real place on it
	const placed =
		getVenuePinData([event])[0]?.precision !== 'unknown' &&
		Boolean(event.location)
	const remaining = seatsRemaining(event)
	const seatsLabel = remaining != null ? `${remaining} ledige` : null
	const passed = isEventPassed(eventEnd(event))
	const tba = arePricesPending(event)

	const cardRef = useRef<HTMLDivElement>(null)
	const [cardOffscreen, setCardOffscreen] = useState(false)

	useEffect(() => {
		const el = cardRef.current
		if (!el || typeof IntersectionObserver === 'undefined') return
		const observer = new IntersectionObserver(([entry]) => {
			// only once the card has gone up and out of view
			setCardOffscreen(
				!entry!.isIntersecting && entry!.boundingClientRect.top < 0,
			)
		})
		observer.observe(el)
		return () => observer.disconnect()
	}, [])

	return (
		<div className="ev">
			<DetailHero event={event} />
			<div className="ev-body">
				{/* one card: under the hero on phones, sticky beside the text on
				    wide screens (placed by the grid, never rendered twice) */}
				<div ref={cardRef} className="ev-card-slot">
					<div className="ev-card-sticky">
						<DetailCtaCard
							event={event}
							isIOS={isIOS}
							initialQuantities={initialQuantities}
							addOnArrival={addOnArrival}
							inCart={inCart}
							reserved={reserved}
							vippsOnline={vippsOnline}
							calendarUrl={calendarUrl}
							shareUrl={shareUrl}
						/>
					</div>
				</div>
				<div className="ev-main">
					<section className="ev-description" aria-label="Om konserten">
						<EventDescription event={event as EventWithSeatsAndTickets} />
					</section>
					{placed ? (
						<section className="ev-map hk" aria-labelledby="ev-map-title">
							<h2 id="ev-map-title" className="ev-section-title">
								Kvar
							</h2>
							<VenueNightMap events={[event]} activeEventId={event.id} />
						</section>
					) : null}
				</div>
			</div>
			{/* a drop-in event has nothing to buy, so no bar pointing at it */}
			{!passed && !event.dropIn ? (
				<StickyCtaBar
					visible={cardOffscreen}
					soldOut={isSoldOut(event)}
					seatsLabel={seatsLabel}
					tba={tba}
				/>
			) : null}
		</div>
	)
}
